import { useEffect, useState } from 'react';

export const DATA_FOCUS_VIEW_PREF = {
    OPEN_IN_THIS_TAB: 'OPEN_IN_THIS_TAB',
    OPEN_IN_A_NEW_TAB: 'OPEN_IN_A_NEW_TAB',
    OPEN_IN_A_POP_UP_WINDOW: 'OPEN_IN_A_POP_UP_WINDOW'
};
export type DataFocusViewPref = keyof typeof DATA_FOCUS_VIEW_PREF;

export const RECORD_VIEW_PREF = {
    OPEN_IN_THIS_TAB: 'OPEN_IN_THIS_TAB',
    OPEN_IN_A_NEW_TAB: 'OPEN_IN_A_NEW_TAB',
    OPEN_IN_A_POP_UP_WINDOW: 'OPEN_IN_A_POP_UP_WINDOW'
};
export type RecordViewPref = keyof typeof RECORD_VIEW_PREF;

// Custom hook to listen for localStorage changes
export const useLocalStorageListener = <T extends string>(key: string, defaultValue: T, validValues: readonly T[]) => {
    const [value, setValue] = useState<T>(() => {
        const stored = localStorage.getItem(key) as T | null;
        return stored && validValues.includes(stored) ? stored : defaultValue;
    });

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === key && e.newValue) {
                const newValue = e.newValue as T;
                if (validValues.includes(newValue)) {
                    setValue(newValue);
                }
            }
        };

        // Listen for localStorage changes from other tabs
        window.addEventListener('storage', handleStorageChange);

        // Create a custom event for changes in the same tab
        const handleCustomStorageChange = (e: CustomEvent) => {
            if (e.detail.key === key && e.detail.value) {
                const newValue = e.detail.value as T;
                if (validValues.includes(newValue)) {
                    setValue(newValue);
                }
            }
        };

        window.addEventListener('localStorageChange', handleCustomStorageChange as EventListener);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('localStorageChange', handleCustomStorageChange as EventListener);
        };
    }, [key, validValues]);

    return value;
};
